import {
  CakeIcon,
  CameraIcon,
  CheckIcon,
  MapPinIcon,
  PaintBrushIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/catalyst/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  useDeleteUserAvatarMutation,
  useUploadUserAvatarMutation,
} from '@/services/api/userService';
import type { User, UserExtra } from '@/services/types/user-types';

const CARD_THEMES = {
  midnight: {
    bg: 'bg-zinc-900',
    gradientFrom: 'from-zinc-800',
    gradientVia: 'via-zinc-900',
    gradientTo: 'to-black',
    avatarGradientFrom: 'from-zinc-700',
    avatarGradientTo: 'to-zinc-800',
    textColor: 'text-white',
    mutedText: 'text-zinc-400',
    mutedTextHover: 'hover:text-white',
    borderColor: 'border-white/10',
    name: 'Midnight',
  },
  ocean: {
    bg: 'bg-blue-950',
    gradientFrom: 'from-blue-900',
    gradientVia: 'via-blue-950',
    gradientTo: 'to-slate-950',
    avatarGradientFrom: 'from-blue-700',
    avatarGradientTo: 'to-blue-900',
    textColor: 'text-white',
    mutedText: 'text-blue-300',
    mutedTextHover: 'hover:text-white',
    borderColor: 'border-white/10',
    name: 'Ocean',
  },
  sunset: {
    bg: 'bg-orange-950',
    gradientFrom: 'from-orange-900',
    gradientVia: 'via-rose-950',
    gradientTo: 'to-purple-950',
    avatarGradientFrom: 'from-orange-700',
    avatarGradientTo: 'to-rose-800',
    textColor: 'text-white',
    mutedText: 'text-orange-300',
    mutedTextHover: 'hover:text-white',
    borderColor: 'border-white/10',
    name: 'Sunset',
  },
  forest: {
    bg: 'bg-emerald-950',
    gradientFrom: 'from-emerald-900',
    gradientVia: 'via-teal-950',
    gradientTo: 'to-slate-950',
    avatarGradientFrom: 'from-emerald-700',
    avatarGradientTo: 'to-teal-800',
    textColor: 'text-white',
    mutedText: 'text-emerald-300',
    mutedTextHover: 'hover:text-white',
    borderColor: 'border-white/10',
    name: 'Forest',
  },
  lavender: {
    bg: 'bg-purple-950',
    gradientFrom: 'from-purple-900',
    gradientVia: 'via-violet-950',
    gradientTo: 'to-indigo-950',
    avatarGradientFrom: 'from-purple-700',
    avatarGradientTo: 'to-violet-800',
    textColor: 'text-white',
    mutedText: 'text-purple-300',
    mutedTextHover: 'hover:text-white',
    borderColor: 'border-white/10',
    name: 'Lavender',
  },
  rose: {
    bg: 'bg-rose-950',
    gradientFrom: 'from-rose-900',
    gradientVia: 'via-pink-950',
    gradientTo: 'to-fuchsia-950',
    avatarGradientFrom: 'from-rose-700',
    avatarGradientTo: 'to-pink-800',
    textColor: 'text-white',
    mutedText: 'text-rose-300',
    mutedTextHover: 'hover:text-white',
    borderColor: 'border-white/10',
    name: 'Rose',
  },
  // Light themes with colored text
  slate: {
    bg: 'bg-slate-100',
    gradientFrom: 'from-slate-200',
    gradientVia: 'via-slate-100',
    gradientTo: 'to-zinc-50',
    avatarGradientFrom: 'from-slate-400',
    avatarGradientTo: 'to-slate-600',
    textColor: 'text-slate-900',
    mutedText: 'text-slate-600',
    mutedTextHover: 'hover:text-slate-900',
    borderColor: 'border-slate-900/10',
    name: 'Slate',
  },
  amber: {
    bg: 'bg-amber-50',
    gradientFrom: 'from-amber-100',
    gradientVia: 'via-yellow-50',
    gradientTo: 'to-orange-100',
    avatarGradientFrom: 'from-amber-400',
    avatarGradientTo: 'to-orange-500',
    textColor: 'text-amber-900',
    mutedText: 'text-amber-700',
    mutedTextHover: 'hover:text-amber-900',
    borderColor: 'border-amber-900/10',
    name: 'Amber',
  },
  emerald: {
    bg: 'bg-emerald-50',
    gradientFrom: 'from-emerald-100',
    gradientVia: 'via-teal-50',
    gradientTo: 'to-cyan-100',
    avatarGradientFrom: 'from-emerald-400',
    avatarGradientTo: 'to-teal-500',
    textColor: 'text-emerald-900',
    mutedText: 'text-emerald-700',
    mutedTextHover: 'hover:text-emerald-900',
    borderColor: 'border-emerald-900/10',
    name: 'Emerald',
  },
  sky: {
    bg: 'bg-sky-50',
    gradientFrom: 'from-sky-100',
    gradientVia: 'via-blue-50',
    gradientTo: 'to-indigo-100',
    avatarGradientFrom: 'from-sky-400',
    avatarGradientTo: 'to-blue-500',
    textColor: 'text-sky-900',
    mutedText: 'text-sky-700',
    mutedTextHover: 'hover:text-sky-900',
    borderColor: 'border-sky-900/10',
    name: 'Sky',
  },
  fuchsia: {
    bg: 'bg-fuchsia-50',
    gradientFrom: 'from-fuchsia-100',
    gradientVia: 'via-pink-50',
    gradientTo: 'to-rose-100',
    avatarGradientFrom: 'from-fuchsia-400',
    avatarGradientTo: 'to-pink-500',
    textColor: 'text-fuchsia-900',
    mutedText: 'text-fuchsia-700',
    mutedTextHover: 'hover:text-fuchsia-900',
    borderColor: 'border-fuchsia-900/10',
    name: 'Fuchsia',
  },
  crimson: {
    bg: 'bg-red-50',
    gradientFrom: 'from-red-100',
    gradientVia: 'via-rose-50',
    gradientTo: 'to-pink-100',
    avatarGradientFrom: 'from-red-400',
    avatarGradientTo: 'to-rose-500',
    textColor: 'text-red-900',
    mutedText: 'text-red-700',
    mutedTextHover: 'hover:text-red-900',
    borderColor: 'border-red-900/10',
    name: 'Crimson',
  },
} as const;

interface UserDetailCardProps {
  user: User;
  isCurrentUser: boolean;
  className?: string;
  onUpdateLocation?: (location: string) => void;
  onUpdateBirthday?: (birthday: string) => void;
  onUpdateTheme?: (theme: UserExtra['card_theme']) => void;
  onUpdateStatus?: (status: string) => void;
}

export const UserDetailCard: React.FC<UserDetailCardProps> = ({
  user,
  isCurrentUser,
  className,
  onUpdateLocation,
  onUpdateBirthday,
  onUpdateTheme,
  onUpdateStatus,
}) => {
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isEditingBirthday, setIsEditingBirthday] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [locationValue, setLocationValue] = useState(
    user.extra?.location || '',
  );
  const [birthdayValue, setBirthdayValue] = useState(
    user.extra?.birthday || '',
  );
  const [statusValue, setStatusValue] = useState(user.extra?.status || '');
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [themePopoverOpen, setThemePopoverOpen] = useState(false);
  const statusTextareaRef = React.useRef<HTMLTextAreaElement>(null);

  const [uploadAvatar, { isLoading: isUploadingAvatar }] =
    useUploadUserAvatarMutation();
  const [deleteAvatar] = useDeleteUserAvatarMutation();

  const theme = CARD_THEMES[user.extra?.card_theme || 'midnight'];

  const displayName =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.username || user.email;

  const initials = user.first_name
    ? `${user.first_name[0]}${user.last_name ? user.last_name[0] : ''}`
    : user.email?.slice(0, 1).toUpperCase();

  // Format created_at to MM/YY
  const finchingSince = useMemo(() => {
    try {
      const date = new Date(user.created_at || Date.now());
      return new Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        year: '2-digit',
      }).format(date);
    } catch {
      return '01/24';
    }
  }, [user.created_at]);

  // Helper to format MM/DD to MMM D for display
  const formattedBirthday = useMemo(() => {
    const bday = user.extra?.birthday;
    if (!bday) return null;
    // Expecting MM/DD
    const [m, d] = bday.split('/');
    if (!m || !d) return bday;

    const date = new Date(2000, parseInt(m, 10) - 1, parseInt(d, 10));
    if (Number.isNaN(date.getTime())) return bday;

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(date);
  }, [user.extra?.birthday]);

  const handleSaveLocation = () => {
    onUpdateLocation?.(locationValue);
    setIsEditingLocation(false);
  };

  const handleSaveBirthday = () => {
    onUpdateBirthday?.(birthdayValue);
    setIsEditingBirthday(false);
  };

  const handleLocationKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveLocation();
    }
    if (e.key === 'Escape') {
      setIsEditingLocation(false);
      setLocationValue(user.extra?.location || '');
    }
  };

  const handleBirthdayKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveBirthday();
    }
    if (e.key === 'Escape') {
      setIsEditingBirthday(false);
      setBirthdayValue(user.extra?.birthday || '');
    }
  };

  const handleUploadAvatar = async (file: File) => {
    try {
      await uploadAvatar({ userId: user.id, file }).unwrap();
      toast.success('Avatar updated');
      setAvatarModalOpen(false);
    } catch {
      toast.error('Failed to upload avatar');
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      await deleteAvatar(user.id).unwrap();
      toast.success('Avatar removed');
    } catch {
      toast.error('Failed to delete avatar');
    }
  };

  const handleThemeChange = (newTheme: UserExtra['card_theme']) => {
    onUpdateTheme?.(newTheme);
    setThemePopoverOpen(false);
    toast.success('Theme updated');
  };

  const handleSaveStatus = () => {
    if (statusValue.trim() !== (user.extra?.status || '')) {
      onUpdateStatus?.(statusValue.trim());
    }
    setIsEditingStatus(false);
  };

  const handleStatusKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveStatus();
    }
    if (e.key === 'Escape') {
      setStatusValue(user.extra?.status || '');
      setIsEditingStatus(false);
    }
  };

  React.useEffect(() => {
    if (isEditingStatus && statusTextareaRef.current) {
      statusTextareaRef.current.style.height = 'auto';
      statusTextareaRef.current.style.height = `${statusTextareaRef.current.scrollHeight}px`;
      statusTextareaRef.current.focus();
    }
  }, [statusValue, isEditingStatus]);

  return (
    <>
      <div
        className={cn(
          'group relative w-full overflow-hidden rounded-3xl transition-colors duration-500',
          theme.bg,
          theme.textColor,
          className,
        )}
        style={{
          isolation: 'isolate', // Prevents border-radius flickering
          backfaceVisibility: 'hidden',
        }}
      >
        {/* Theme Button - Top Right */}
        {isCurrentUser && (
          <Popover open={themePopoverOpen} onOpenChange={setThemePopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/10 text-white backdrop-blur-md transition-all hover:scale-110 hover:bg-white/20 opacity-0 group-hover:opacity-100"
                title="Change theme"
              >
                <PaintBrushIcon className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-2 bg-white/95 backdrop-blur-xl border-zinc-200"
              align="end"
            >
              <div className="flex gap-2 flex-wrap">
                {(
                  Object.keys(CARD_THEMES) as Array<keyof typeof CARD_THEMES>
                ).map((themeKey) => {
                  const t = CARD_THEMES[themeKey];
                  const isActive =
                    (user.extra?.card_theme || 'midnight') === themeKey;
                  return (
                    <button
                      key={themeKey}
                      type="button"
                      onClick={() => handleThemeChange(themeKey)}
                      className={cn(
                        'group relative rounded-lg transition-all hover:scale-110',
                        isActive && 'ring-2 ring-zinc-900 ring-offset-2',
                      )}
                      title={t.name}
                    >
                      <div
                        className={cn(
                          'h-12 w-12 rounded-lg shadow-md overflow-hidden',
                        )}
                      >
                        <div
                          className={cn(
                            'h-full w-full bg-gradient-to-br',
                            t.gradientFrom,
                            t.gradientVia,
                            t.gradientTo,
                          )}
                        >
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                        </div>
                      </div>
                      {isActive && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="h-4 w-4 rounded-full bg-white flex items-center justify-center shadow-lg">
                            <CheckIcon className="h-2.5 w-2.5 text-zinc-900" />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Subtle texture & gradient background */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-overlay" />
        <div
          className={cn(
            'absolute inset-0 bg-gradient-to-br opacity-90',
            theme.gradientFrom,
            theme.gradientVia,
            theme.gradientTo,
          )}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

        {/* Content Container */}
        <div className="relative flex flex-col p-8 sm:p-10">
          <div className="flex items-start justify-between gap-6">
            {/* Avatar Section */}
            <div className="relative shrink-0">
              <div
                className={cn(
                  'group/avatar relative rounded-2xl bg-gradient-to-br p-[3px] shadow-lg transition-transform duration-300 hover:scale-[1.02]',
                  theme.avatarGradientFrom,
                  theme.avatarGradientTo,
                )}
              >
                <Avatar
                  className="h-28 w-28 rounded-[14px] ring-0 transition-opacity duration-300 group-hover/avatar:opacity-90 sm:h-32 sm:w-32"
                  src={user.avatar || undefined}
                  initials={initials}
                />

                {isCurrentUser && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-[14px] bg-black/40 opacity-0 backdrop-blur-[2px] transition-all duration-200 group-hover/avatar:opacity-100">
                    <button
                      type="button"
                      onClick={() => setAvatarModalOpen(true)}
                      className="absolute inset-0 z-10"
                    >
                      <div className="sr-only">Upload Avatar</div>
                    </button>
                    <div className="flex gap-2 translate-y-2 opacity-0 transition-all duration-200 group-hover/avatar:translate-y-0 group-hover/avatar:opacity-100 z-20">
                      <button
                        type="button"
                        onClick={() => setAvatarModalOpen(true)}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-transform hover:scale-110 hover:bg-white/20"
                      >
                        <CameraIcon className="h-4 w-4" />
                      </button>
                      {user.avatar && (
                        <button
                          type="button"
                          onClick={handleDeleteAvatar}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/10 text-red-400 backdrop-blur-md transition-transform hover:scale-110 hover:bg-red-500/20"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Main Info */}
            <div className="flex-1 min-w-0 pt-2">
              <h1
                className={cn(
                  'truncate text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl font-display pb-1',
                  theme.textColor,
                )}
              >
                {displayName}
              </h1>

              {/* Location */}
              {(user.extra?.location || isCurrentUser) && (
                <div className="mt-3 flex items-center gap-2">
                  <MapPinIcon className="h-4 w-4 text-zinc-500" />
                  {isCurrentUser && isEditingLocation ? (
                    <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-200">
                      <input
                        type="text"
                        value={locationValue}
                        onChange={(e) => setLocationValue(e.target.value)}
                        onKeyDown={handleLocationKeyDown}
                        className="w-48 bg-transparent border-b border-zinc-600 py-0.5 text-base font-medium text-white focus:border-white focus:outline-none placeholder:text-zinc-600"
                        placeholder="Set location..."
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleSaveLocation}
                        className="text-emerald-400 hover:text-emerald-300 transition-colors"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditingLocation(false)}
                        className="text-rose-400 hover:text-rose-300 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={!isCurrentUser}
                      onClick={() =>
                        isCurrentUser && setIsEditingLocation(true)
                      }
                      className={cn(
                        'group/loc flex items-center gap-2 text-base font-medium transition-colors',
                        isCurrentUser
                          ? 'hover:text-white cursor-pointer'
                          : 'cursor-default',
                        !user.extra?.location
                          ? 'text-zinc-500 italic'
                          : 'text-zinc-400',
                      )}
                    >
                      <span>{user.extra?.location || 'Add Location'}</span>
                      {isCurrentUser && (
                        <PencilIcon className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover/loc:opacity-100 group-hover/loc:translate-x-0 transition-all duration-200 text-zinc-500" />
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Birthday */}
              {(user.extra?.birthday || isCurrentUser) && (
                <div className="mt-1 flex items-center gap-2">
                  <CakeIcon className="h-4 w-4 text-zinc-500" />
                  {isCurrentUser && isEditingBirthday ? (
                    <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-200">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d{2}/\d{2}"
                        maxLength={5}
                        value={birthdayValue}
                        onChange={(e) => {
                          let val = e.target.value.replace(/\D/g, '');
                          if (val.length > 4) val = val.slice(0, 4);
                          if (val.length > 2) {
                            val = `${val.slice(0, 2)}/${val.slice(2)}`;
                          }
                          setBirthdayValue(val);
                        }}
                        onKeyDown={handleBirthdayKeyDown}
                        className="w-48 bg-transparent border-b border-zinc-600 py-0.5 text-base font-medium text-white focus:border-white focus:outline-none placeholder:text-zinc-600"
                        placeholder="MM/DD"
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleSaveBirthday}
                        className="text-emerald-400 hover:text-emerald-300 transition-colors"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditingBirthday(false)}
                        className="text-rose-400 hover:text-rose-300 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={!isCurrentUser}
                      onClick={() =>
                        isCurrentUser && setIsEditingBirthday(true)
                      }
                      className={cn(
                        'group/bday flex items-center gap-2 text-base font-medium transition-colors',
                        isCurrentUser
                          ? 'hover:text-white cursor-pointer'
                          : 'cursor-default',
                        !user.extra?.birthday
                          ? 'text-zinc-500 italic'
                          : 'text-zinc-400',
                      )}
                    >
                      <span>{formattedBirthday || 'Add Birthday'}</span>
                      {isCurrentUser && (
                        <PencilIcon className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover/bday:opacity-100 group-hover/bday:translate-x-0 transition-all duration-200 text-zinc-500" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer / Contact Details */}
          <div
            className={cn(
              'mt-8 flex flex-wrap items-end justify-between gap-6 border-t pt-6',
              theme.borderColor,
            )}
          >
            <div className="flex flex-col gap-1">
              <div
                className={cn(
                  'text-[0.65rem] font-bold uppercase tracking-wider',
                  theme.mutedText,
                )}
              >
                Contact
              </div>
              <div
                className={cn(
                  'flex flex-col gap-0.5 font-mono text-sm',
                  theme.mutedText,
                )}
              >
                <a
                  href={`mailto:${user.email}`}
                  className={cn(
                    'transition-colors truncate max-w-[200px] sm:max-w-xs',
                    theme.mutedTextHover,
                  )}
                >
                  {user.email}
                </a>
                {user.phone_numbers?.[0] && (
                  <a
                    href={`tel:${user.phone_numbers[0].phone_number}`}
                    className={cn('transition-colors', theme.mutedTextHover)}
                  >
                    {user.phone_numbers[0].phone_number}
                  </a>
                )}
              </div>
            </div>

            <div className="text-right">
              <div
                className={cn(
                  'text-[0.65rem] font-bold uppercase tracking-wider mb-1',
                  theme.mutedText,
                )}
              >
                Finching Since
              </div>
              <div className={cn('font-mono text-sm', theme.mutedText)}>
                {finchingSince}
              </div>
            </div>
          </div>

          {/* Quote - Very Bottom */}
          {(user.extra?.status || isCurrentUser) && (
            <div className={cn('mt-6 pt-4 border-t', theme.borderColor)}>
              {isEditingStatus ? (
                <div className="space-y-2 animate-in fade-in duration-200">
                  <textarea
                    ref={statusTextareaRef}
                    value={statusValue}
                    onChange={(e) => setStatusValue(e.target.value)}
                    onKeyDown={handleStatusKeyDown}
                    maxLength={140}
                    rows={1}
                    className={cn(
                      'w-full resize-none bg-transparent p-0 text-sm italic placeholder:opacity-30 focus:outline-none focus:ring-0',
                      theme.mutedText,
                    )}
                    placeholder="Set a quote..."
                  />
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'text-[9px] font-semibold tracking-wider uppercase opacity-50',
                        statusValue.length > 120 && 'text-orange-400',
                      )}
                    >
                      {statusValue.length}/140
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          setStatusValue(user.extra?.status || '');
                          setIsEditingStatus(false);
                        }}
                        className="rounded-full p-1 text-white/40 transition-colors hover:bg-white/10 hover:text-white/70"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveStatus}
                        className="rounded-full bg-white/10 p-1 text-white/70 transition-all hover:scale-105 hover:bg-white/20 hover:text-white active:scale-95"
                      >
                        <CheckIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => isCurrentUser && setIsEditingStatus(true)}
                  disabled={!isCurrentUser}
                  className={cn(
                    'group w-full text-left transition-opacity',
                    isCurrentUser && 'cursor-pointer hover:opacity-70',
                  )}
                >
                  {user.extra?.status ? (
                    <p
                      className={cn(
                        'text-sm leading-relaxed italic opacity-60',
                        theme.mutedText,
                      )}
                    >
                      &ldquo;{user.extra.status}&rdquo;
                    </p>
                  ) : (
                    isCurrentUser && (
                      <p
                        className={cn(
                          'text-sm leading-relaxed italic opacity-40',
                          theme.mutedText,
                        )}
                      >
                        Set a quote...
                      </p>
                    )
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <AvatarUploadModal
        isOpen={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        onUpload={handleUploadAvatar}
        isUploading={isUploadingAvatar}
      />
    </>
  );
};

interface AvatarUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  isUploading: boolean;
}

const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  isUploading,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
    },
    maxFiles: 1,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0 bg-white/95 backdrop-blur-xl border-zinc-200">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <DialogTitle className="text-lg font-bold tracking-tight text-zinc-900">
            Upload Profile Picture
          </DialogTitle>
          <p className="text-sm text-zinc-500">
            Choose a new avatar for your profile.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="p-6">
            {!selectedFile ? (
              <div
                {...getRootProps()}
                className={cn(
                  'group relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50/50 py-12 text-center transition-all hover:bg-white hover:border-zinc-300 cursor-pointer',
                  isDragActive && 'border-zinc-900 bg-zinc-50',
                )}
              >
                <input {...getInputProps()} />
                <div className="mb-4 rounded-full bg-white p-4 shadow-sm ring-1 ring-zinc-100 transition-transform group-hover:scale-110">
                  <CameraIcon className="h-6 w-6 text-zinc-400 group-hover:text-zinc-900" />
                </div>
                <p className="text-sm font-semibold text-zinc-900">
                  Click to upload or drag and drop
                </p>
                <p className="mt-1 text-xs text-zinc-500">
                  SVG, PNG, JPG or GIF (max 2MB)
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="relative overflow-hidden rounded-2xl bg-zinc-900 py-8">
                  {preview && (
                    <img
                      src={preview}
                      alt="Preview"
                      className="mx-auto h-32 w-32 rounded-full object-cover ring-4 ring-white/10"
                    />
                  )}
                </div>
                <div className="flex items-center justify-between rounded-lg border border-zinc-200 p-3">
                  <div className="flex flex-col overflow-hidden">
                    <span className="truncate text-sm font-medium text-zinc-900">
                      {selectedFile.name}
                    </span>
                    <span className="text-xs text-zinc-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null);
                      setPreview(null);
                    }}
                    className="text-zinc-500 hover:text-zinc-900"
                  >
                    Change
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 bg-zinc-50/50 px-6 py-4 border-t border-zinc-100">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isUploading}
              className="border-zinc-200 text-zinc-700 hover:bg-white hover:text-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!selectedFile || isUploading}
              className={cn(
                'min-w-[100px] bg-zinc-900 text-white hover:bg-zinc-800',
                isUploading && 'opacity-80',
              )}
            >
              {isUploading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                  <span>Saving...</span>
                </div>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
